import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  AdminRole,
  AdminUser,
  ApiMetaResponse,
  AnalyticsArea,
  AreaCompareResponse,
  AreaHeatmapResponse,
  AreaSummaryResponse,
  AreaTimelineResponse,
  AuthFailureAdmin,
  AgroCropCycle,
  AgroCropType,
  AgroZoneConfig,
  DeviceAdmin,
  DeviceKeyAdmin,
  DeviceLive,
  LoginResponse,
  MeasurementStatsResponse,
  StructureAccount,
  StructureSite,
  StructureZone,
  DeviceAssignment,
  PublicOverview,
  UserProfile
} from "../shared/models";

function resolveBaseUrl(): string {
  if (typeof window === "undefined") {
    return "/api/v1";
  }

  const host = window.location.hostname.toLowerCase();
  const isLocal = host === "localhost" || host === "127.0.0.1" || host === "::1";
  if (isLocal) {
    return "/api/v1";
  }

  return "https://api.smartbirds.app/api/v1";
}

const BASE_URL = resolveBaseUrl();

@Injectable({ providedIn: "root" })
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  login(emailOrUsername: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${BASE_URL}/auth/login`, { email: emailOrUsername, username: emailOrUsername, password });
  }

  getMyProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${BASE_URL}/auth/me`);
  }

  updateMyProfile(payload: { displayName?: string; avatarUrl?: string }): Observable<UserProfile> {
    return this.http.patch<UserProfile>(`${BASE_URL}/auth/me`, payload);
  }

  getApiMeta(): Observable<ApiMetaResponse> {
    return this.http.get<ApiMetaResponse>(`${BASE_URL}/meta`);
  }

  getOverview(): Observable<PublicOverview> {
    return this.http.get<PublicOverview>(`${BASE_URL}/public/overview`);
  }

  getLiveDevices(): Observable<{ data: DeviceLive[]; total: number }> {
    return this.http.get<{ data: DeviceLive[]; total: number }>(`${BASE_URL}/public/devices/live`);
  }

  getAdminDevices(): Observable<{ data: DeviceAdmin[]; total: number }> {
    return this.http.get<{ data: DeviceAdmin[]; total: number }>(`${BASE_URL}/admin/devices`);
  }

  getAdminDevice(deviceId: string): Observable<DeviceAdmin> {
    return this.http.get<DeviceAdmin>(`${BASE_URL}/admin/devices/${encodeURIComponent(deviceId)}`);
  }

  deleteAdminDevice(deviceId: string): Observable<{ deleted: boolean; deviceId: string }> {
    return this.http.delete<{ deleted: boolean; deviceId: string }>(`${BASE_URL}/admin/devices/${encodeURIComponent(deviceId)}`);
  }

  updateAdminDevice(
    deviceId: string,
    payload: { displayName?: string | null; manualLocation?: { lat: number; lon: number } | null }
  ): Observable<DeviceAdmin> {
    return this.http.patch<DeviceAdmin>(`${BASE_URL}/admin/devices/${encodeURIComponent(deviceId)}`, payload);
  }

  getAdminDeviceReadings(
    deviceId: string,
    limit = 100
  ): Observable<{
    totalReadings: number;
    latest: Array<{
      readAt: string;
      networkType: string;
      lat?: number;
      lon?: number;
      airQualityIndex: number;
      noiseDb: number;
      temperatureC: number;
      humidityPct: number;
      eco2Ppm?: number;
      tvocPpb?: number;
      lightLux?: number;
      batteryPct?: number;
      firmwareVersion?: string;
    }>;
  }> {
    return this.http.get<{
      totalReadings: number;
      latest: Array<{
        readAt: string;
        networkType: string;
        lat?: number;
        lon?: number;
        airQualityIndex: number;
        noiseDb: number;
        temperatureC: number;
        humidityPct: number;
        eco2Ppm?: number;
        tvocPpb?: number;
        lightLux?: number;
        batteryPct?: number;
        firmwareVersion?: string;
      }>;
    }>(`${BASE_URL}/admin/devices/${encodeURIComponent(deviceId)}/readings?limit=${limit}`);
  }

  getDeviceKeys(): Observable<{ data: DeviceKeyAdmin[]; total: number }> {
    return this.http.get<{ data: DeviceKeyAdmin[]; total: number }>(`${BASE_URL}/admin/device-keys`);
  }

  createDeviceKey(deviceId: string, description?: string): Observable<{ deviceId: string; deviceKey: string; note: string }> {
    return this.http.post<{ deviceId: string; deviceKey: string; note: string }>(`${BASE_URL}/admin/device-keys`, {
      deviceId,
      description
    });
  }

  revokeDeviceKey(deviceId: string): Observable<{ revoked: boolean; deviceId: string; revokedAt: string }> {
    return this.http.post<{ revoked: boolean; deviceId: string; revokedAt: string }>(
      `${BASE_URL}/admin/device-keys/${encodeURIComponent(deviceId)}/revoke`,
      {}
    );
  }

  getAuthFailures(limit = 100): Observable<{ data: AuthFailureAdmin[]; total: number }> {
    return this.http.get<{ data: AuthFailureAdmin[]; total: number }>(`${BASE_URL}/admin/auth-failures?limit=${limit}`);
  }

  clearAuthFailures(): Observable<{ cleared: boolean; deleted: number }> {
    return this.http.delete<{ cleared: boolean; deleted: number }>(`${BASE_URL}/admin/auth-failures`);
  }

  getUsers(): Observable<{ data: AdminUser[]; total: number }> {
    return this.http.get<{ data: AdminUser[]; total: number }>(`${BASE_URL}/admin/users`);
  }

  createUser(payload: {
    username: string;
    displayName?: string;
    avatarUrl?: string;
    password: string;
    role: string;
    permissions: string[];
    isActive: boolean;
  }): Observable<{ created: boolean; username: string }> {
    return this.http.post<{ created: boolean; username: string }>(`${BASE_URL}/admin/users`, payload);
  }

  updateUser(
    username: string,
    payload: { displayName?: string | null; avatarUrl?: string | null; password?: string; role?: string; permissions?: string[]; isActive?: boolean }
  ): Observable<{ updated: boolean; username: string }> {
    return this.http.patch<{ updated: boolean; username: string }>(`${BASE_URL}/admin/users/${encodeURIComponent(username)}`, payload);
  }

  deleteUser(username: string): Observable<{ deleted: boolean; username: string }> {
    return this.http.delete<{ deleted: boolean; username: string }>(`${BASE_URL}/admin/users/${encodeURIComponent(username)}`);
  }

  getRoles(): Observable<{ data: AdminRole[]; total: number }> {
    return this.http.get<{ data: AdminRole[]; total: number }>(`${BASE_URL}/admin/roles`);
  }

  saveRole(payload: { roleKey: string; displayName: string; permissions: string[] }): Observable<{ saved: boolean; roleKey: string }> {
    return this.http.post<{ saved: boolean; roleKey: string }>(`${BASE_URL}/admin/roles`, payload);
  }

  updateRole(roleKey: string, payload: { displayName: string; permissions: string[] }): Observable<{ updated: boolean; roleKey: string }> {
    return this.http.patch<{ updated: boolean; roleKey: string }>(`${BASE_URL}/admin/roles/${encodeURIComponent(roleKey)}`, {
      roleKey,
      ...payload
    });
  }

  deleteRole(roleKey: string): Observable<{ deleted: boolean; roleKey: string }> {
    return this.http.delete<{ deleted: boolean; roleKey: string }>(`${BASE_URL}/admin/roles/${encodeURIComponent(roleKey)}`);
  }

  listStructureAccounts(): Observable<{ data: StructureAccount[]; total: number }> {
    return this.http.get<{ data: StructureAccount[]; total: number }>(`${BASE_URL}/admin/entities/accounts`);
  }

  createStructureAccount(payload: {
    code?: string;
    name: string;
    accountType?: string;
    isActive?: boolean;
  }): Observable<StructureAccount> {
    return this.http.post<StructureAccount>(`${BASE_URL}/admin/entities/accounts`, payload);
  }

  updateStructureAccount(id: number, payload: { code?: string; name: string; accountType?: string; isActive?: boolean }): Observable<{ updated: boolean; id: number }> {
    return this.http.patch<{ updated: boolean; id: number }>(`${BASE_URL}/admin/entities/accounts/${id}`, payload);
  }

  deleteStructureAccount(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${BASE_URL}/admin/entities/accounts/${id}`);
  }

  listStructureSites(accountId?: number): Observable<{ data: StructureSite[]; total: number }> {
    const suffix = accountId ? `?accountId=${accountId}` : "";
    return this.http.get<{ data: StructureSite[]; total: number }>(`${BASE_URL}/admin/entities/sites${suffix}`);
  }

  createStructureSite(payload: {
    accountId: number;
    code?: string;
    name: string;
    siteType?: string;
    lat?: number;
    lon?: number;
  }): Observable<StructureSite> {
    return this.http.post<StructureSite>(`${BASE_URL}/admin/entities/sites`, payload);
  }

  updateStructureSite(
    id: number,
    payload: { accountId: number; code?: string; name: string; siteType?: string; lat?: number; lon?: number }
  ): Observable<{ updated: boolean; id: number }> {
    return this.http.patch<{ updated: boolean; id: number }>(`${BASE_URL}/admin/entities/sites/${id}`, payload);
  }

  deleteStructureSite(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${BASE_URL}/admin/entities/sites/${id}`);
  }

  listStructureZones(siteId?: number): Observable<{ data: StructureZone[]; total: number }> {
    const suffix = siteId ? `?siteId=${siteId}` : "";
    return this.http.get<{ data: StructureZone[]; total: number }>(`${BASE_URL}/admin/entities/zones${suffix}`);
  }

  createStructureZone(payload: {
    siteId: number;
    code?: string;
    name: string;
    zoneType?: string;
    polygon?: Array<{ lat: number; lon: number }>;
  }): Observable<StructureZone> {
    return this.http.post<StructureZone>(`${BASE_URL}/admin/entities/zones`, payload);
  }

  updateStructureZone(id: number, payload: { siteId: number; code?: string; name: string; zoneType?: string; polygon?: Array<{ lat: number; lon: number }> }): Observable<{ updated: boolean; id: number }> {
    return this.http.patch<{ updated: boolean; id: number }>(`${BASE_URL}/admin/entities/zones/${id}`, payload);
  }

  deleteStructureZone(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${BASE_URL}/admin/entities/zones/${id}`);
  }

  listDeviceAssignments(activeOnly = true): Observable<{ data: DeviceAssignment[]; total: number }> {
    return this.http.get<{ data: DeviceAssignment[]; total: number }>(
      `${BASE_URL}/admin/entities/device-assignments?activeOnly=${activeOnly ? "true" : "false"}`
    );
  }

  assignDevice(payload: {
    deviceId: string;
    accountId?: number;
    siteId?: number;
    zoneId?: number;
    notes?: string;
  }): Observable<{ assigned: boolean; deviceId: string }> {
    return this.http.post<{ assigned: boolean; deviceId: string }>(`${BASE_URL}/admin/entities/device-assignments`, payload);
  }

  unassignDeviceAssignment(id: number): Observable<{ unassigned: boolean; id: number }> {
    return this.http.delete<{ unassigned: boolean; id: number }>(`${BASE_URL}/admin/entities/device-assignments/${id}`);
  }

  listAgroCropTypes(): Observable<{ data: AgroCropType[]; total: number }> {
    return this.http.get<{ data: AgroCropType[]; total: number }>(`${BASE_URL}/admin/agro/crop-types`);
  }

  createAgroCropType(payload: { code?: string; name: string; category?: string }): Observable<AgroCropType> {
    return this.http.post<AgroCropType>(`${BASE_URL}/admin/agro/crop-types`, payload);
  }

  deleteAgroCropType(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${BASE_URL}/admin/agro/crop-types/${id}`);
  }

  updateAgroCropType(id: number, payload: { code?: string; name: string; category?: string }): Observable<{ updated: boolean; id: number }> {
    return this.http.patch<{ updated: boolean; id: number }>(`${BASE_URL}/admin/agro/crop-types/${id}`, payload);
  }

  listAgroCropCycles(zoneId?: number): Observable<{ data: AgroCropCycle[]; total: number }> {
    const suffix = zoneId ? `?zoneId=${zoneId}` : "";
    return this.http.get<{ data: AgroCropCycle[]; total: number }>(`${BASE_URL}/admin/agro/crop-cycles${suffix}`);
  }

  createAgroCropCycle(payload: { zoneId: number; cropTypeId: number; name: string; startDate: string; endDate?: string; status?: string; notes?: string }): Observable<AgroCropCycle> {
    return this.http.post<AgroCropCycle>(`${BASE_URL}/admin/agro/crop-cycles`, payload);
  }

  deleteAgroCropCycle(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${BASE_URL}/admin/agro/crop-cycles/${id}`);
  }

  updateAgroCropCycle(id: number, payload: { zoneId: number; cropTypeId: number; name: string; startDate: string; endDate?: string; status?: string; notes?: string }): Observable<{ updated: boolean; id: number }> {
    return this.http.patch<{ updated: boolean; id: number }>(`${BASE_URL}/admin/agro/crop-cycles/${id}`, payload);
  }

  listAgroZoneConfigs(zoneId?: number): Observable<{ data: AgroZoneConfig[]; total: number }> {
    const suffix = zoneId ? `?zoneId=${zoneId}` : "";
    return this.http.get<{ data: AgroZoneConfig[]; total: number }>(`${BASE_URL}/admin/agro/zone-configs${suffix}`);
  }

  createAgroZoneConfig(payload: { zoneId: number; cropTypeId?: number; name: string; thresholds: Record<string, unknown>; isActive?: boolean }): Observable<AgroZoneConfig> {
    return this.http.post<AgroZoneConfig>(`${BASE_URL}/admin/agro/zone-configs`, payload);
  }

  deleteAgroZoneConfig(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${BASE_URL}/admin/agro/zone-configs/${id}`);
  }


  updateAgroZoneConfig(id: number, payload: { zoneId: number; cropTypeId?: number; name: string; thresholds: Record<string, unknown>; isActive?: boolean }): Observable<{ updated: boolean; id: number }> {
    return this.http.patch<{ updated: boolean; id: number }>(`${BASE_URL}/admin/agro/zone-configs/${id}`, payload);
  }

  getAgroDashboard(params: { from: string; to: string; siteId?: number; zoneId?: number }): Observable<{ from: string; to: string; data: any[]; total: number }> {
    const site = params.siteId ? `&siteId=${params.siteId}` : "";
    const zone = params.zoneId ? `&zoneId=${params.zoneId}` : "";
    return this.http.get<{ from: string; to: string; data: any[]; total: number }>(
      `${BASE_URL}/admin/agro/dashboard?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}${site}${zone}`
    );
  }

  getAgroMapLayer(params: { from: string; to: string; metric: string }): Observable<{ from: string; to: string; metric: string; data: any[]; total: number }> {
    return this.http.get<{ from: string; to: string; metric: string; data: any[]; total: number }>(
      `${BASE_URL}/admin/agro/map-layer?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}&metric=${encodeURIComponent(params.metric)}`
    );
  }

  getAgroAlerts(params: { from: string; to: string }): Observable<{ from: string; to: string; data: any[]; total: number }> {
    return this.http.get<{ from: string; to: string; data: any[]; total: number }>(
      `${BASE_URL}/admin/agro/alerts?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}`
    );
  }

  getAgroScouting(params: { from: string; to: string }): Observable<{ from: string; to: string; data: any[]; total: number }> {
    return this.http.get<{ from: string; to: string; data: any[]; total: number }>(
      `${BASE_URL}/admin/agro/scouting?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}`
    );
  }

  getAgroReport(params: { from: string; to: string; accountId?: number; siteId?: number; zoneId?: number }): Observable<{ from: string; to: string; data: any[]; total: number }> {
    const account = params.accountId ? `&accountId=${params.accountId}` : "";
    const site = params.siteId ? `&siteId=${params.siteId}` : "";
    const zone = params.zoneId ? `&zoneId=${params.zoneId}` : "";
    return this.http.get<{ from: string; to: string; data: any[]; total: number }>(
      `${BASE_URL}/admin/agro/reports?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}${account}${site}${zone}`
    );
  }

  getAgroReportTimeline(params: {
    from: string;
    to: string;
    metric: "eco2Ppm" | "tvocPpb" | "noiseDb" | "temperatureC" | "humidityPct" | "lightLux";
    bucket: "hour" | "day" | "week" | "month";
    zoneIds?: number[];
  }): Observable<{ from: string; to: string; metric: string; bucket: string; data: Array<{ zoneId: number; zoneName: string; slot: string; avg: number | null; count: number }>; total: number }> {
    const zoneIds = params.zoneIds && params.zoneIds.length ? `&zoneIds=${encodeURIComponent(params.zoneIds.join(","))}` : "";
    return this.http.get<{ from: string; to: string; metric: string; bucket: string; data: Array<{ zoneId: number; zoneName: string; slot: string; avg: number | null; count: number }>; total: number }>(
      `${BASE_URL}/admin/agro/reports/timeline?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}&metric=${encodeURIComponent(params.metric)}&bucket=${encodeURIComponent(params.bucket)}${zoneIds}`
    );
  }

  getMeasurementStatistics(
    params: { range: "live" | "day" | "week" | "month" | "year"; metric: "airQualityIndex" | "noiseDb" | "temperatureC" | "humidityPct" | "eco2Ppm" | "tvocPpb" | "lightLux"; limit?: number; from?: string; to?: string }
  ): Observable<MeasurementStatsResponse> {
    const limit = params.limit ?? 1200;
    const from = params.from ? `&from=${encodeURIComponent(params.from)}` : "";
    const to = params.to ? `&to=${encodeURIComponent(params.to)}` : "";
    return this.http.get<MeasurementStatsResponse>(
      `${BASE_URL}/admin/statistics/measurements?range=${params.range}&metric=${params.metric}&limit=${limit}${from}${to}`
    );
  }

  listAnalyticsAreas(): Observable<{ data: AnalyticsArea[]; total: number }> {
    return this.http.get<{ data: AnalyticsArea[]; total: number }>(`${BASE_URL}/admin/statistics/areas`);
  }

  createAnalyticsArea(payload: { name: string; polygon: Array<{ lat: number; lon: number }> }): Observable<AnalyticsArea> {
    return this.http.post<AnalyticsArea>(`${BASE_URL}/admin/statistics/areas`, payload);
  }

  updateAnalyticsArea(areaId: number, payload: { name?: string; polygon?: Array<{ lat: number; lon: number }> }): Observable<AnalyticsArea> {
    return this.http.patch<AnalyticsArea>(`${BASE_URL}/admin/statistics/areas/${areaId}`, payload);
  }

  deleteAnalyticsArea(areaId: number): Observable<{ deleted: boolean; areaId: number }> {
    return this.http.delete<{ deleted: boolean; areaId: number }>(`${BASE_URL}/admin/statistics/areas/${areaId}`);
  }

  getAreaSummary(areaId: number, from: string, to: string): Observable<AreaSummaryResponse> {
    return this.http.get<AreaSummaryResponse>(
      `${BASE_URL}/admin/statistics/areas/${areaId}/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
  }

  getAreaTimeline(
    areaId: number,
    params: { from: string; to: string; metric: "noiseDb" | "eco2Ppm" | "tvocPpb"; bucket: "hour" | "day" | "week" | "month" }
  ): Observable<AreaTimelineResponse> {
    return this.http.get<AreaTimelineResponse>(
      `${BASE_URL}/admin/statistics/areas/${areaId}/timeline?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}&metric=${params.metric}&bucket=${params.bucket}`
    );
  }

  getAreaHeatmap(
    areaId: number,
    params: { from: string; to: string; metric: "noiseDb" | "eco2Ppm" | "tvocPpb"; precision: number }
  ): Observable<AreaHeatmapResponse> {
    return this.http.get<AreaHeatmapResponse>(
      `${BASE_URL}/admin/statistics/areas/${areaId}/heatmap?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}&metric=${params.metric}&precision=${params.precision}`
    );
  }

  getAreaCompare(
    areaId: number,
    params: { metric: "noiseDb" | "eco2Ppm" | "tvocPpb"; from: string; to: string; prevFrom: string; prevTo: string }
  ): Observable<AreaCompareResponse> {
    return this.http.get<AreaCompareResponse>(
      `${BASE_URL}/admin/statistics/areas/${areaId}/compare?metric=${params.metric}&from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}&prevFrom=${encodeURIComponent(params.prevFrom)}&prevTo=${encodeURIComponent(params.prevTo)}`
    );
  }

  createDeviceCommand(
    deviceId: string,
    payload: {
      type:
        | "set_sample_ms"
        | "set_tx_ms"
        | "set_warmup_ms"
        | "set_sensor_power"
        | "set_gas_publish_ms"
        | "set_power_mode"
        | "set_sleep_config"
        | "set_telemetry_policy"
        | "set_noise_config"
        | "sync_config"
        | "reboot";
      params: Record<string, unknown>;
      ttlSec: number;
      priority: "low" | "normal" | "high";
    }
  ): Observable<{ commandId: string; status: string; createdAt: string }> {
    return this.http.post<{ commandId: string; status: string; createdAt: string }>(
      `${BASE_URL}/devices/${encodeURIComponent(deviceId)}/commands`,
      payload
    );
  }

  getDeviceConfig(deviceId: string): Observable<{
    deviceId: string;
    configVersion: number;
    config: {
      sampleMs?: number;
      txMs?: number;
      warmupMs?: number;
      gasPublishMs?: number;
      gasMinValidSamples?: number;
      sensorPower?: {
        ens160Enabled?: boolean;
        aht21Enabled?: boolean;
        veml7700Enabled?: boolean;
        noiseEnabled?: boolean;
        scd30Enabled?: boolean;
        scd41Enabled?: boolean;
        gpsEnabled?: boolean;
        si7021Enabled?: boolean;
      };
      powerProfile?: string;
      powerMode?: string;
      sleepConfig?: { enabled?: boolean; sleepMs?: number; awakeWindowMs?: number; keepRxWindowAfterTxMs?: number };
      telemetryPolicy?: { sendEmptyMetrics?: boolean; heartbeatEnabled?: boolean; heartbeatMs?: number };
      noiseConfig?: { enabled?: boolean; algorithm?: string; emaAlpha?: number };
      configVersion?: number;
    };
    updatedAt: string;
  }> {
    return this.http.get<{
      deviceId: string;
      configVersion: number;
      config: {
        sampleMs?: number;
        txMs?: number;
        warmupMs?: number;
        gasPublishMs?: number;
        gasMinValidSamples?: number;
        sensorPower?: {
          ens160Enabled?: boolean;
          aht21Enabled?: boolean;
          veml7700Enabled?: boolean;
          noiseEnabled?: boolean;
          scd30Enabled?: boolean;
          scd41Enabled?: boolean;
          gpsEnabled?: boolean;
          si7021Enabled?: boolean;
        };
        powerProfile?: string;
        powerMode?: string;
        sleepConfig?: { enabled?: boolean; sleepMs?: number; awakeWindowMs?: number; keepRxWindowAfterTxMs?: number };
        telemetryPolicy?: { sendEmptyMetrics?: boolean; heartbeatEnabled?: boolean; heartbeatMs?: number };
        noiseConfig?: { enabled?: boolean; algorithm?: string; emaAlpha?: number };
        configVersion?: number;
      };
      updatedAt: string;
    }>(`${BASE_URL}/devices/${encodeURIComponent(deviceId)}/config`);
  }

  getDeviceCommands(
    deviceId: string,
    params?: { status?: "pending" | "applied" | "rejected" | "failed" | "expired"; limit?: number }
  ): Observable<{
    deviceId: string;
    data: Array<{
      commandId: string;
      type: string;
      status: string;
      params: Record<string, unknown>;
      priority: string;
      ttlSec: number;
      configVersion?: number;
      result?: Record<string, unknown>;
      createdAt: string;
      expiresAt: string;
      ackedAt?: string;
    }>;
    total: number;
  }> {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.limit) q.set("limit", String(params.limit));
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return this.http.get<{
      deviceId: string;
      data: Array<{
        commandId: string;
        type: string;
        status: string;
        params: Record<string, unknown>;
        priority: string;
        ttlSec: number;
        configVersion?: number;
        result?: Record<string, unknown>;
        createdAt: string;
        expiresAt: string;
        ackedAt?: string;
      }>;
      total: number;
    }>(`${BASE_URL}/devices/${encodeURIComponent(deviceId)}/commands${suffix}`);
  }

  cancelDeviceCommand(deviceId: string, commandId: string): Observable<{ canceled: boolean; commandId: string; deviceId: string }> {
    return this.http.post<{ canceled: boolean; commandId: string; deviceId: string }>(
      `${BASE_URL}/devices/${encodeURIComponent(deviceId)}/commands/${encodeURIComponent(commandId)}/cancel`,
      {}
    );
  }

  getProvisionClaims(status?: "pending" | "approved" | "rejected" | "expired"): Observable<{
    data: Array<{
      claimId: number;
      deviceId: string;
      hardwareId: string;
      fwVersion?: string;
      gatewayId?: string;
      status: string;
      notes?: string;
      expiresAt: string;
      createdAt: string;
      updatedAt: string;
      approvedBy?: string;
      approvedAt?: string;
    }>;
    total: number;
  }> {
    const suffix = status ? `?status=${status}` : "";
    return this.http.get<{
      data: Array<{
        claimId: number;
        deviceId: string;
        hardwareId: string;
        fwVersion?: string;
        gatewayId?: string;
        status: string;
        notes?: string;
        expiresAt: string;
        createdAt: string;
        updatedAt: string;
        approvedBy?: string;
        approvedAt?: string;
      }>;
      total: number;
    }>(`${BASE_URL}/admin/provision/claims${suffix}`);
  }

  approveProvisionClaim(claimId: number, notes?: string): Observable<{ approved: boolean; claimId: number }> {
    return this.http.post<{ approved: boolean; claimId: number }>(`${BASE_URL}/admin/provision/claims/${claimId}/approve`, { notes });
  }

  rejectProvisionClaim(claimId: number, notes?: string): Observable<{ rejected: boolean; claimId: number }> {
    return this.http.post<{ rejected: boolean; claimId: number }>(`${BASE_URL}/admin/provision/claims/${claimId}/reject`, { notes });
  }
}
